/**
 * English catalogue for EPIC APT.
 *
 * Arabic is the source language; anything missing here falls back to it, so an
 * absent key renders a usable screen rather than `apt.book.title`.
 */
export const appointmentsEn: Record<string, string> = {
  'apt.title': 'Appointments',
  'apt.subtitle': '{count} appointments · {branch}',
  'apt.viewList': 'List',
  'apt.viewWeek': 'Week',
  'apt.print': 'Print schedule',
  'apt.export': 'Export',
  'apt.new': '+ New appointment',
  'apt.today': 'Today',
  'apt.filter.waiting': 'Waiting',
  'apt.filter.noShow': 'No-show',
  'apt.filter.public': 'Online',
  'apt.filter.doctor': 'Doctor',
  'apt.filter.service': 'Service',
  'apt.filter.branch': 'Branch',
  'apt.filter.all': 'All',
  'apt.filter.status': 'Status',
  'apt.filter.source': 'Source',
  'apt.filter.clear': 'Clear filters',
  'apt.search.placeholder': 'Search by name, MRN or phone…',

  'apt.col.time': 'Time',
  'apt.col.patient': 'Patient',
  'apt.col.doctor': 'Doctor',
  'apt.col.service': 'Service',
  'apt.col.source': 'Source',
  'apt.col.status': 'Status',
  'apt.col.action': 'Action',
  'apt.count': '{shown} of {total} appointments',
  'apt.prev': 'Previous',
  'apt.next': 'Next',

  'apt.action.confirm': 'Confirm',
  'apt.action.arrive': 'Check in',
  'apt.action.start': 'Start consultation',
  'apt.action.ready': 'Ready for checkout',
  'apt.action.checkout': 'Checkout',
  'apt.action.view': 'View',
  'apt.action.reschedule': 'Reschedule',
  'apt.action.cancel': 'Cancel',
  'apt.action.noShow': 'No-show',
  'apt.action.none': '—',

  'apt.source.STAFF': 'Reception',
  'apt.source.PUBLIC': 'Online',
  'apt.source.WALK_IN': 'Walk-in',
  'apt.source.FOLLOW_UP': 'Follow-up',
  'apt.source.IMPORT': 'Import',

  'apt.empty.title': 'No appointments on this day',
  'apt.empty.body': 'Change the date or filters, or book a new appointment.',
  'apt.empty.filtered.title': 'No matching results',
  'apt.empty.filtered.body': 'Try broader filters, or clear them all.',

  'apt.book.title': 'New appointment',
  'apt.book.patient': 'Patient',
  'apt.book.searchPatient': 'Search by name, MRN or phone…',
  'apt.book.quickCreate': '+ Quick-create a patient',
  'apt.book.quickCreateTitle': 'New provisional contact',
  'apt.book.contactName': 'Full name',
  'apt.book.contactPhone': 'Mobile number',
  'apt.book.contactNote':
    'Recorded as a provisional contact. No patient file is created until check-in.',
  'apt.book.doctor': 'Doctor',
  'apt.book.type': 'Appointment type',
  'apt.book.service': 'Service',
  'apt.book.serviceNone': 'No specific service',
  'apt.book.date': 'Date',
  'apt.book.slots': 'Available times',
  'apt.book.noSlots': 'No times available on this day',
  'apt.book.reason': 'Reason for visit',
  'apt.book.reasonPlaceholder': 'Optional…',
  'apt.book.feePolicy': 'Fee policy',
  'apt.book.submit': 'Confirm booking',
  'apt.book.cancel': 'Cancel',
  'apt.book.asRequest': 'Save as a request pending confirmation',
  'apt.book.selectPatient': 'Choose a patient or create a contact first',
  'apt.book.selectSlot': 'Choose an available time',
  'apt.book.conflict': 'That time was just taken. Choose another.',
  'apt.book.balanceWarning': 'This patient has an outstanding balance of {amount}',
  'apt.book.clinicalWarning': 'This patient has recorded clinical alerts',
  'apt.book.sameDayWarning':
    'This patient has another appointment the same day — a patient clash is a warning only.',

  'apt.fee.STANDARD': 'Consultation',
  'apt.fee.FOLLOW_UP': 'Follow-up',
  'apt.fee.EXEMPT': 'Exempt',
  'apt.fee.FREE': 'Free',
  'apt.fee.SERVICE_PRICE': 'Service price',

  'apt.type.FIRST_VISIT': 'First visit',
  'apt.type.FOLLOW_UP': 'Follow-up',
  'apt.type.PROCEDURE': 'Procedure',
  'apt.type.CONSULTATION': 'Consultation',
  'apt.type.EMERGENCY': 'Emergency',

  'apt.cancel.title': 'Cancel appointment',
  'apt.cancel.reason': 'Cancellation reason',
  'apt.cancel.reasonRequired': 'A reason is required and is recorded on the appointment.',
  'apt.cancel.submit': 'Confirm cancellation',
  'apt.noShow.title': 'Mark as no-show',
  'apt.noShow.reason': 'No-show reason',
  'apt.noShow.submit': 'Confirm',
  'apt.reschedule.title': 'Reschedule appointment',
  'apt.reschedule.current': 'Current time',
  'apt.reschedule.submit': 'Move appointment',
  'apt.dialog.close': 'Close',

  'apt.week.title': 'Week calendar',
  'apt.week.prev': 'Previous week',
  'apt.week.next': 'Next week',
  'apt.week.legend.confirmed': 'Confirmed',
  'apt.week.legend.requested': 'Pending confirmation',
  'apt.week.legend.closed': 'Break / closed',
  'apt.week.legend.leave': 'Leave',
  'apt.week.pickDoctor': 'Choose a doctor to show the calendar',

  'apt.public.step.branch': 'Choose a branch and doctor',
  'apt.public.step.service': 'Choose a service',
  'apt.public.step.time': 'Choose a time',
  'apt.public.step.details': 'Your details',
  'apt.public.branch': 'Branch',
  'apt.public.doctor': 'Doctor',
  'apt.public.service': 'Service',
  'apt.public.morning': 'Morning',
  'apt.public.afternoon': 'Afternoon',
  'apt.public.noSlots': 'No times available on this day',
  'apt.public.fullName': 'Full name',
  'apt.public.namePlaceholder': 'Enter your name…',
  'apt.public.phone': 'Mobile number',
  'apt.public.phonePlaceholder': '05X XXX XXXX',
  'apt.public.consent': 'I agree to the {policy} and to being contacted about this appointment.',
  'apt.public.privacyNotice': 'privacy notice',
  'apt.public.submit': 'Confirm booking request',
  'apt.public.submitting': 'Submitting…',
  'apt.public.reviewNote': 'Reception will review your request. We will confirm by SMS.',
  'apt.public.autoNote': 'Your appointment is confirmed. Details will be sent to your mobile.',
  'apt.public.back': 'Back',
  'apt.public.continue': 'Continue',
  'apt.public.nameRequired': 'Your name is required',
  'apt.public.phoneRequired': 'A mobile number is required',
  'apt.public.consentRequired': 'You must accept the privacy notice',
  'apt.public.slotRequired': 'Choose an available time',
  'apt.public.rateLimited': 'Too many attempts. Please try again shortly.',
  'apt.public.slotTaken': 'That time was just taken. Choose another.',
  'apt.public.closed': 'Online booking is not available for this clinic.',
  'apt.public.unavailable.HOLIDAY': 'Public holiday',
  'apt.public.unavailable.DOCTOR_ON_LEAVE': 'Doctor on leave',
  'apt.public.unavailable.NO_DOCTOR_SCHEDULE': 'Doctor not working',
  'apt.public.unavailable.BRANCH_CLOSED': 'Branch closed',

  'apt.confirm.title': 'Request received',
  'apt.confirm.titleConfirmed': 'Appointment confirmed',
  'apt.confirm.body': 'Reception will confirm your appointment within one working hour.',
  'apt.confirm.bodyConfirmed': 'Your appointment is confirmed. Please arrive ten minutes early.',
  'apt.confirm.reference': 'Reference',
  'apt.confirm.doctor': 'Doctor',
  'apt.confirm.datetime': 'Date and time',
  'apt.confirm.branch': 'Branch',
  'apt.confirm.status': 'Status',
  'apt.confirm.policy':
    'Cancellation policy: please cancel at least {hours} hours in advance. Not attending without notice may incur a fee.',
  'apt.confirm.addToCalendar': 'Add to calendar',
  'apt.confirm.bookAnother': 'Book another appointment',
  'apt.confirm.keepReference': 'Keep your reference number for reception.',
};
